<template>
    <div>
        <button
            ref="triggerRef"
            v-tooltip="{
                content: group.name,
                delay: { show: 1250, hide: 150 },
            }"
            type="button"
            class="group block w-full"
            :aria-expanded="isOpen"
            @click="toggle"
        >
            <div
                class="flex h-12 w-full items-center rounded-[6.75px] px-4 py-3 transition-all duration-200"
                :class="[
                    showsLabels === true ? 'gap-7' : 'justify-center',
                    isGroupActive(group) === true
                        ? 'bg-gray-200 text-gray-600'
                        : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600',
                ]"
                :data-helper-tooltip="group.name.toLocaleLowerCase()"
            >
                <component
                    :is="group.icon"
                    class="h-5 w-5 shrink-0 transition duration-200 group-hover:text-gray-600"
                />

                <template v-if="showsLabels === true">
                    <span class="truncate text-base font-medium">
                        {{ group.name }}
                    </span>

                    <ChevronDownIcon
                        class="ml-auto h-4 w-4 shrink-0 transition-transform duration-200"
                        :class="{ 'rotate-180': isOpen }"
                    />
                </template>
            </div>
        </button>

        <div
            v-if="showsLabels === true && isOpen === true"
            class="mt-[5px] space-y-[5px]"
        >
            <inertia-link
                v-for="child in group.children"
                :key="child.href"
                :href="child.href"
                :aria-current="isLinkActive(child) === true ? 'page' : null"
                class="group block w-full"
                @click="$emit('navigate')"
            >
                <div
                    class="flex h-10 w-full items-center gap-4 rounded-[6.75px] py-2 pl-11 pr-4 transition-all duration-200"
                    :class="
                        isLinkActive(child) === true
                            ? 'bg-gray-200 text-gray-600'
                            : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600'
                    "
                >
                    <component :is="child.icon" class="h-4 w-4 shrink-0" />

                    <span class="truncate text-sm font-medium">
                        {{ child.name }}
                    </span>
                </div>
            </inertia-link>
        </div>

        <teleport to="body">
            <div
                v-if="showsLabels === false && isOpen === true"
                ref="flyoutRef"
                class="fixed z-50 w-[210px] rounded-[6.75px] border border-gray-200 bg-white p-[5px] shadow-lg"
                :style="flyoutStyle"
            >
                <inertia-link
                    v-for="child in group.children"
                    :key="child.href"
                    :href="child.href"
                    :aria-current="isLinkActive(child) === true ? 'page' : null"
                    class="group block w-full"
                    @click="close"
                >
                    <div
                        class="flex h-10 w-full items-center gap-3 rounded-[6.75px] px-3 transition-all duration-200"
                        :class="
                            isLinkActive(child) === true
                                ? 'bg-gray-200 text-gray-600'
                                : 'text-gray-500 hover:bg-gray-200 hover:text-gray-600'
                        "
                    >
                        <component :is="child.icon" class="h-4 w-4 shrink-0" />

                        <span class="truncate text-sm font-medium">
                            {{ child.name }}
                        </span>
                    </div>
                </inertia-link>
            </div>
        </teleport>
    </div>
</template>

<script lang="ts" setup>
import { computed, onBeforeUnmount, onMounted, ref, watch } from "vue";
import type { CSSProperties } from "vue";
import { ChevronDownIcon } from "@heroicons/vue/24/outline";
import type { ILinkGroup } from "@/data/sidebar";

const props = defineProps({
    group: {
        type: Object as () => ILinkGroup,
        required: true,
    },
    opened: {
        type: Boolean,
        default: false,
    },
    compact: {
        type: Boolean,
        default: false,
    },
});

defineEmits(["navigate"]);

const { isLinkActive, isGroupActive } = sidebar();

const isXl = ref(false);
const isOpen = ref(false);
const triggerRef = ref<HTMLButtonElement | null>(null);
const flyoutRef = ref<HTMLElement | null>(null);
const flyoutStyle = ref<CSSProperties>({});

const showsLabels = computed((): boolean => {
    return props.compact === false && (props.opened === true || isXl.value);
});

const updateIsXl = (): void => {
    isXl.value = window.matchMedia("(min-width: 1326px)").matches;
};

const positionFlyout = (): void => {
    const trigger = triggerRef.value;

    if (trigger === null) {
        return;
    }

    const rect = trigger.getBoundingClientRect();

    flyoutStyle.value = {
        top: `${rect.top}px`,
        left: `${rect.right + 8}px`,
    };
};

const close = (): void => {
    isOpen.value = false;
};

const toggle = (): void => {
    isOpen.value = isOpen.value === false;

    if (isOpen.value === true && showsLabels.value === false) {
        positionFlyout();
    }
};

const handleOutsideClick = (event: MouseEvent): void => {
    if (isOpen.value === false || showsLabels.value === true) {
        return;
    }

    const target = event.target as Node;

    if (triggerRef.value?.contains(target) === true) {
        return;
    }

    if (flyoutRef.value?.contains(target) === true) {
        return;
    }

    close();
};

const handleViewportChange = (): void => {
    updateIsXl();

    if (isOpen.value === true && showsLabels.value === false) {
        positionFlyout();
    }
};

watch(
    [showsLabels, (): boolean => isGroupActive(props.group)],
    ([labelsVisible, groupActive]: [boolean, boolean]): void => {
        isOpen.value = labelsVisible === true && groupActive === true;
    },
);

onMounted(() => {
    updateIsXl();
    isOpen.value =
        showsLabels.value === true && isGroupActive(props.group) === true;

    window.addEventListener("resize", handleViewportChange, { passive: true });
    window.addEventListener("scroll", handleViewportChange, {
        passive: true,
        capture: true,
    });
    document.addEventListener("click", handleOutsideClick);
});

onBeforeUnmount(() => {
    window.removeEventListener("resize", handleViewportChange);
    window.removeEventListener("scroll", handleViewportChange, {
        capture: true,
    });
    document.removeEventListener("click", handleOutsideClick);
});
</script>

<style></style>
